import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, TextField } from '@mui/material';

import { Color } from '@/types';
import { useUpdateColorMutation } from '@/services';
import { messages } from '@/utils/helpers';

export interface UpdateColorProps extends Omit<DialogProps, 'color'> {
  color: Color;
  onClose(): void;
}

const UpdateColor: React.FC<UpdateColorProps> = ({ color, onClose, ...props }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [updateColor, updateColorResponse] = useUpdateColorMutation();

  const { values, errors, touched, handleSubmit, handleChange } = useFormik<Color>({
    initialValues: color ?? {
      name: '',
      hex: '',
    },
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      name: yup.string().required(messages.form.REQUIRED),
      hex: yup.string().required(messages.form.REQUIRED),
    }),
    async onSubmit(color, { resetForm }) {
      try {
        await updateColor(color).unwrap();

        onClose();
        resetForm();
      } catch (error) {
        enqueueSnackbar(error.data, { variant: 'error' });
      }
    },
  });

  return (
    <Dialog fullWidth {...props}>
      <DialogTitle fontWeight="bold">Редактировать цвет</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="name"
                label="Наименование"
                placeholder="Введите значение"
                value={values.name}
                onChange={handleChange}
                disabled={updateColorResponse.isLoading}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name ? errors.name : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="hex"
                label="Цвет"
                type="color"
                placeholder="Введите значение"
                value={values.hex}
                onChange={handleChange}
                disabled={updateColorResponse.isLoading}
                error={touched.hex && Boolean(errors.hex)}
                helperText={touched.hex ? errors.hex : ''}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            disabled={updateColorResponse.isLoading}
            onClick={onClose}
          >
            Закрыть
          </Button>

          <Button type="submit" variant="contained" disabled={updateColorResponse.isLoading}>
            Редактировать
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateColor;
